/**
 * Członkowie rodziny zgłaszani do ubezpieczenia zdrowotnego (Etap 30, ADR-0031).
 *
 * Pracownik nie zgłasza rodziny sam: robi to PŁATNIK na formularzu ZUS ZCNA,
 * w ciągu 7 dni od dnia, w którym ubezpieczony poinformował go o konieczności
 * zgłoszenia (poradnik ZUS „Ogólne zasady wypełniania i korygowania dokumentów
 * ubezpieczeniowych", sierpień 2023, rozdział I pkt 3.2). Termin liczy się więc
 * od ZGŁOSZENIA PRZEZ PRACOWNIKA, a nie od dnia urodzenia dziecka czy ślubu —
 * i to jest data, którą trzeba zapisać jako fakt.
 *
 * Za członka rodziny — dla celów ubezpieczenia zdrowotnego — uważa się osobę
 * NIEPODLEGAJĄCĄ temu ubezpieczeniu z innego tytułu: dziecko (własne, małżonka,
 * przysposobione, wnuka albo dziecko obce w opiece lub rodzinie zastępczej) do
 * 18 lat, a gdy się uczy — do 26 lat, z orzeczeniem o znacznym stopniu
 * niepełnosprawności bez ograniczenia wieku; małżonka; wstępnych pozostających
 * z ubezpieczonym we wspólnym gospodarstwie domowym (art. 5 pkt 3 ustawy
 * o świadczeniach opieki zdrowotnej finansowanych ze środków publicznych).
 *
 * O tym, czy warunek „niepodlegania z innego tytułu" jest spełniony, system nie
 * orzeka — nie wie, czy dorosłe dziecko nie podjęło pracy. To oświadczenie
 * pracownika, a nasza rola to zapisać je i dopilnować terminu.
 */

/**
 * Kod stopnia pokrewieństwa / powinowactwa — dwa znaki z wykazu ZUS
 * (poradnik „Ogólne zasady…", rozdział „Kody", część II).
 */
export interface KinshipCode {
  readonly code: string
  readonly name: string
}

export const KINSHIP_CODES: readonly KinshipCode[] = [
  { code: '01', name: 'małżonek' },
  { code: '11', name: 'dziecko własne, przysposobione lub dziecko małżonka' },
  {
    code: '21',
    name: 'wnuk albo dziecko obce w opiece, rodzinie zastępczej lub rodzinnym domu dziecka',
  },
  { code: '30', name: 'matka' },
  { code: '31', name: 'ojciec' },
  { code: '32', name: 'macocha' },
  { code: '33', name: 'ojczym' },
  { code: '40', name: 'babka' },
  { code: '41', name: 'dziadek' },
  { code: '50', name: 'osoba przysposabiająca ubezpieczonego' },
  { code: '60', name: 'inny wstępny we wspólnym gospodarstwie domowym' },
]

export function kinshipName(code: string | null | undefined): string | null {
  if (code === null || code === undefined) return null
  return KINSHIP_CODES.find((entry) => entry.code === code)?.name ?? null
}

export function isKinshipCode(value: string | null | undefined): boolean {
  return kinshipName(value) !== null
}

/**
 * Kod stopnia niepełnosprawności — jeden znak z tego samego wykazu. Zero nie
 * znaczy „zdrowy": znaczy „nie ma orzeczenia albo go nie przedłożono płatnikowi".
 */
export interface DisabilityDegree {
  readonly code: string
  readonly name: string
}

export const DISABILITY_DEGREES: readonly DisabilityDegree[] = [
  { code: '0', name: 'bez orzeczenia albo orzeczenie nieprzedłożone płatnikowi' },
  { code: '1', name: 'lekki stopień niepełnosprawności' },
  { code: '2', name: 'umiarkowany stopień niepełnosprawności' },
  { code: '3', name: 'znaczny stopień niepełnosprawności' },
  { code: '4', name: 'orzeczenie o niepełnosprawności dla osoby do 16. roku życia' },
]

export function isDisabilityDegree(value: string | null | undefined): boolean {
  return value !== null && value !== undefined && DISABILITY_DEGREES.some((d) => d.code === value)
}

/**
 * Rodzaj dokumentu tożsamości członka rodziny — wypełniany TYLKO wtedy, gdy
 * osoba nie ma numeru PESEL (pola 05 i 06 formularza).
 */
export const FAMILY_DOCUMENT_KINDS = [
  { code: '1', name: 'dowód osobisty' },
  { code: '2', name: 'paszport albo inny dokument tożsamości' },
] as const
export type FamilyDocumentKind = (typeof FAMILY_DOCUMENT_KINDS)[number]['code']

/** Zdarzenie członka rodziny: uzyskanie albo utrata uprawnień (pole 01). */
export const FAMILY_EVENTS = ['zgloszenie', 'wyrejestrowanie'] as const
export type FamilyEvent = (typeof FAMILY_EVENTS)[number]

/** Wartość pola 01 formularza dla zdarzenia: 1 = zgłoszenie, 2 = utrata. */
export function familyEventCode(event: FamilyEvent): '1' | '2' {
  return event === 'zgloszenie' ? '1' : '2'
}

/** Czy członek rodziny jest zgłoszony (nie utracił uprawnień). */
export function familyMemberActive(entitlementTo: string | null | undefined): boolean {
  return entitlementTo === null || entitlementTo === undefined
}
